import { useCallback, useMemo, useState, MouseEvent } from "react"
import { GetApp } from "../../App"
import { UserAdviceInstance, UserAdviceLocation, UserAdviceState } from "../../Views/UserAdvice/Advice"
import { classNames } from "../../Components"
import { Icon, Icons } from "../Icon"
import { UserAdviceData } from "./Data"
import { UserAdvicePlacement, UserAdviceWidth } from "./Params"

interface UserAdviceRowProps {
  advices: UserAdviceInstance[];
  className?: string;
  // Renders the row already expanded -- for catalog/demo fixtures only; production
  // always starts collapsed, and collapses again on every re-render of the log row.
  initExpanded?: boolean;
  state: UserAdviceState;
  // The width tier to lay out for. Passed in rather than measured here so the row
  // stays deterministic in the catalog and unit tests; production derives it from the
  // same media queries the stylesheet uses.
  width: UserAdviceWidth;
}

// The strip of advice buttons on a log row (#1171). It decides how much wording each
// button may show -- see `UserAdviceData.adviceFormats` -- and offers the expand
// toggle that spells them all out, because the collapsed row deliberately clips
// anything that doesn't fit rather than wrapping onto a second line.
export function UserAdviceRow({ advices, className, initExpanded, state, width }: UserAdviceRowProps) {
  const app = GetApp();
  const t = app.getT("UserAdviceRow");
  const [expanded, setExpanded] = useState(initExpanded ?? false);

  const toggle = useCallback((e: MouseEvent) => {
    e.preventDefault();
    e.stopPropagation();
    setExpanded((value) => !value);
  }, []);

  const formats = useMemo(() => {
    return UserAdviceData.adviceFormats(advices.map((advice) => ({
      id: advice.id,
      level: advice.props.level,
      placement: advice.location == UserAdviceLocation.day ? UserAdvicePlacement.day : UserAdvicePlacement.log,
    })), width, expanded);
  }, [advices, width, expanded]);

  if (advices.length == 0) {
    return null;
  }
  // The toggle only earns its own space once something is actually being held back:
  // with every button already at its full wording there is nothing left to expand to.
  const expandable = expanded || formats.some((format) => format != "full");
  return (
    <>
      {advices.map((advice, index) => (
        <div className="UserAdviceRow-advice" key={advice.id}>{advice.renderButton(state, formats[index])}</div>
      ))}
      {expandable && (
        <button
          aria-expanded={expanded}
          aria-label={t(expanded ? "collapse" : "expand")}
          className={classNames(["UserAdviceRow-toggle", "button", className])}
          onClick={toggle}
          title={t(expanded ? "collapse" : "expand")}
        >
          <Icon icon={expanded ? Icons.ArrowsLeft : Icons.ArrowsRight} />
        </button>
      )}
    </>
  );
}

// The width tier the advice row lays out for, from the same breakpoints as
// `_breakpoints.scss` ($breakpoint-small 450px, $breakpoint-mobile 768px).
export function userAdviceWidth(): UserAdviceWidth {
  if (typeof window == "undefined" || !window.matchMedia) {
    return UserAdviceWidth.desktop;
  }
  if (window.matchMedia("(max-width: 449px)").matches) {
    return UserAdviceWidth.small;
  }
  if (window.matchMedia("(max-width: 767px)").matches) {
    return UserAdviceWidth.mobile;
  }
  return UserAdviceWidth.desktop;
}
